import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Cursor, CursorBody, CursorName, CursorPointer } from "@/components/ui/cursor";

interface RemoteCursor {
  id: string;
  name: string;
  color: string;
  x: number; // normalized 0..1 within the room element
  y: number;
  ts: number;
}

const PALETTE = [
  "#e11d48", "#f59e0b", "#10b981", "#3b82f6",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316",
];

function guestIdentity() {
  if (typeof window === "undefined") return { id: "ssr", name: "Guest", color: PALETTE[0] };
  const KEY = "pluggz.guest";
  try {
    const cached = window.localStorage.getItem(KEY);
    if (cached) return JSON.parse(cached) as { id: string; name: string; color: string };
  } catch { /* noop */ }
  const id = crypto.randomUUID();
  const name = `Guest ${Math.floor(Math.random() * 9000 + 1000)}`;
  const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
  const ident = { id, name, color };
  try { window.localStorage.setItem(KEY, JSON.stringify(ident)); } catch { /* noop */ }
  return ident;
}

interface Props {
  /** Stable room id. Users in the same room see each other. */
  roomId: string;
}

/**
 * Realtime multiplayer cursors scoped to `roomId`. Renders inside a
 * positioned parent — the parent should be `relative`. Cursors are
 * pointer-events:none so they never block clicks.
 */
export function LiveCursors({ roomId }: Props) {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [remotes, setRemotes] = useState<Record<string, RemoteCursor>>({});
  const [wsAvailable, setWsAvailable] = useState(true);
  const meRef = useRef<{ id: string; name: string; color: string } | null>(null);

  // Build identity once on the client
  useEffect(() => {
    if (typeof WebSocket === "undefined") {
      setWsAvailable(false);
      return;
    }
    const guest = guestIdentity();
    meRef.current = {
      id: user?.id ?? guest.id,
      name: user?.user_metadata?.full_name || user?.email?.split("@")[0] || guest.name,
      color: guest.color,
    };
  }, [user]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof WebSocket === "undefined") return;
    if (!meRef.current) {
      const guest = guestIdentity();
      meRef.current = { id: user?.id ?? guest.id, name: guest.name, color: guest.color };
    }
    const me = meRef.current;
    const container = containerRef.current?.parentElement;
    if (!container) return;

    const channel = supabase.channel(`cursors:${roomId}`, {
      config: { broadcast: { self: false }, presence: { key: me.id } },
    });
    let subscribed = false;

    channel
      .on("broadcast", { event: "cursor" }, ({ payload }) => {
        const c = payload as RemoteCursor;
        if (!c?.id || c.id === me.id) return;
        setRemotes((prev) => ({ ...prev, [c.id]: { ...c, ts: Date.now() } }));
      })
      .on("broadcast", { event: "leave" }, ({ payload }) => {
        const id = (payload as { id: string })?.id;
        if (!id) return;
        setRemotes((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      })
      .on("presence", { event: "leave" }, ({ leftPresences }) => {
        setRemotes((prev) => {
          const next = { ...prev };
          for (const p of leftPresences as Array<{ id?: string }>) {
            if (p.id) delete next[p.id];
          }
          return next;
        });
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          subscribed = true;
          await channel.track({ id: me.id, name: me.name, color: me.color });
        } else {
          subscribed = false;
        }
      });

    // Throttle pointermove broadcasts to ~30fps
    let lastSent = 0;
    let scheduled: number | null = null;
    let lastX = 0;
    let lastY = 0;
    const send = () => {
      scheduled = null;
      lastSent = Date.now();
      if (!subscribed) return;
      void channel.send({
        type: "broadcast",
        event: "cursor",
        payload: { id: me.id, name: me.name, color: me.color, x: lastX, y: lastY, ts: lastSent },
      });
    };
    const onMove = (e: PointerEvent) => {
      const r = container.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      lastX = (e.clientX - r.left) / r.width;
      lastY = (e.clientY - r.top) / r.height;
      if (lastX < 0 || lastX > 1 || lastY < 0 || lastY > 1) return;
      const now = Date.now();
      const since = now - lastSent;
      if (since >= 33) send();
      else if (scheduled == null) scheduled = window.setTimeout(send, 33 - since);
    };
    const onLeave = () => {
      if (!subscribed) return;
      void channel.send({ type: "broadcast", event: "leave", payload: { id: me.id } });
    };

    container.addEventListener("pointermove", onMove, { passive: true });
    container.addEventListener("pointerleave", onLeave, { passive: true });
    window.addEventListener("beforeunload", onLeave);

    // Garbage-collect stale remote cursors every 5s (no update for 10s)
    const gc = window.setInterval(() => {
      const cutoff = Date.now() - 10_000;
      setRemotes((prev) => {
        let changed = false;
        const next: typeof prev = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v.ts >= cutoff) next[k] = v;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 5000);

    return () => {
      container.removeEventListener("pointermove", onMove);
      container.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("beforeunload", onLeave);
      if (scheduled) window.clearTimeout(scheduled);
      window.clearInterval(gc);
      onLeave();
      void supabase.removeChannel(channel);
    };
  }, [roomId, user]);

  if (!wsAvailable) return null;

  try {
    return (
      <div
        ref={containerRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 z-50 overflow-hidden"
      >
        {Object.values(remotes).map((c) => (
          <div
            key={c.id}
            style={{
              position: "absolute",
              left: `${(c.x * 100).toFixed(2)}%`,
              top: `${(c.y * 100).toFixed(2)}%`,
              transform: "translate(-2px, -2px)",
              transition: "left 80ms linear, top 80ms linear",
              color: c.color,
            }}
          >
            <Cursor>
              <CursorPointer />
              <CursorBody style={{ backgroundColor: c.color, color: "#fff" }}>
                <CursorName>{c.name}</CursorName>
              </CursorBody>
            </Cursor>
          </div>
        ))}
      </div>
    );
  } catch {
    return null;
  }
}